import { z } from 'zod';

import type { BulkUploadItem, BulkUploadResponse } from '@apple/utils/files/models/bulk-upload';
import {
	bulkUploadItemSchema,
	createBulkUploadResponseSchema,
} from '@apple/utils/files/models/bulk-upload';
import type { WeekdayOption } from '@apple/utils/globalization/components/days-of-week/model';
import { weekdayOptionSchema } from '@apple/utils/globalization/components/days-of-week/model';
import type { CountryCode } from '@apple/utils/globalization/models';
import { countryCodeSchema, dateTimeSchema } from '@apple/utils/globalization/models';
import { paginatedListDtoSchema } from '@apple/utils/pagination/models';

import type { CustomerGroupModel } from '@apple/features/customer/management.models';
import { customerGroupSchema } from '@apple/features/customer/management.models';

/** The store/customer third party id (the code that apple uses) */
export type StoreId = string;

/** @see: AppleRetail.Web.Features.Stores.StoreListingController.StoreListingModel */
export interface StoreListingModel {
	id: string;
	name: string;
	vintage: string | null;
	code: string;
	streetAddress1: string | '';
	streetAddress2: string | '';
	streetAddress3: string | '';
	city: string | '';
	stateOrProvince: string | '';
	postalCode: string | '';
	countryCode: CountryCode | null;
	customerGroupId: string;
	customerGroupName: string;
	status: string;
	activeDate: Date | null;
	inactiveDate: Date | null;
	storeNumber: string | '';
	logoImageUrl: string | null;
	thirdPartyId: StoreId;
	timeZone: string;
	costCenter: string | null;
	goLiveDate: Date | null;
	expeditedShippingFee: number | null;
	recipientName: string | null;
	attentionTo: string | null;
	warehouseLocationId: string;
	utcOffsetMinutes: number;
	locationType: LocationType;
}

export type LocationType = z.infer<typeof locationTypeSchema>;

/** AppleRetail.Web.Features.Stores.StoreListingController.StoreListingRequest */
export type StoreRequest = z.infer<typeof storeRequestSchema>;

/** AppleRetail.Web.Features.Stores.StoreListingController.StoreListingResponse */
export type StoreListingResponse = z.infer<typeof storeListingResponseSchema>;

/** AppleRetail.Web.Features.Stores.StoreListingController.Market */
export type Market = z.infer<typeof marketSchema>;

export interface BaseStoreModel {
	id: StoreId;
	thirdPartyCustomerId: StoreId;
	customerCode: string;
	countryCode: CountryCode;
}

/** @see: AppleRetail.Web.Features.Stores.StoreModel */
export interface StoreModel extends BaseStoreModel {
	name: string;
	vintageId: number | null;
	streetAddress1: string;
	streetAddress2: string | null;
	streetAddress3: string | null;
	city: string;
	stateOrProvince: string | null;
	postalCode: string;
	activeDate: Date | null;
	inactiveDate: Date | null;
	logoImageUrl: string | null;
	costCenter: string | null;
	recipientName: string;
	attentionTo: string | null;
	goLiveDate: Date | null;
	timeZone: string;
	phone: string | null;
	countryCodes: CountryCode[];
	// Should rename property when old front-end retired
	customerClassification: LocationType;
	customerGroup: CustomerGroupModel;
	currencyCode: string;
	fiscalYearBudgets: FiscalYearBudget[];
	cultureCode: string | null;
	emailAddress: string;
	shipmentDays: WeekdayOption[];
	weekDays: WeekdayOption[];
	cnpj: string | null;
	ie: string | null;
	isIeExempt: boolean;
	orderCutoffTime: number;
	cityStatePostalDisplay: string;
	countryNameDisplay: string;
	storeNumber: string;
	isDeleted: boolean;
}

export interface StoreModelRequest extends Omit<StoreModel, 'id' | 'customerClassification'> {
	id: StoreId | null;
	customerClassification: LocationType | null;
}
export interface StoreFormData
	extends Omit<
		StoreModel,
		| 'recipientName'
		| 'name'
		| 'streetAddress1'
		| 'streetAddress2'
		| 'streetAddress3'
		| 'stateOrProvince'
		| 'countryCode'
		| 'thirdPartyCustomerId'
		| 'phone'
		| 'logoImageUrl'
		| 'customerClassification'
	> {
	recipient: string;
	storeId: string;
	storeName: string;
	addressLineOne: string;
	addressLineTwo: string | null;
	addressLineThree: string | null;
	stateProvinceRegion: string | null;
	country: CountryCode;
	phoneNumber: string;
	imageFileName: string | null;
	locationType: LocationType;
}

/** @see: AppleRetail.Web.Models.FiscalYearBudgetModel */
export interface FiscalYearBudget {
	fiscalYear: number;
	storedBudgets: FiscalPeriodBudget[];
}

/** @see: AppleRetail.Web.Models.StoreBudgetModel */
export interface FiscalPeriodBudget {
	periodNumber: number;
	allocatedAmount: number;
	consumedAmount: number;
}

/** @see: Manzanita.Web.Models.CustomerAutoCompleteModel */
export interface StoreAutoCompleteModel extends BaseStoreModel {
	displayString: string;
}

/** @see: AppleRetail.Web.Areas.Admin.Models.Bulk.StoreBulkUploadItemViewModel */
export interface StoreBulkUploadItem extends BulkUploadItem {}

export type StoreBulkUploadResponse = BulkUploadResponse<StoreBulkUploadItem>;

/** @see: Manzanita.Web.Models.CustomerAutoCompleteModel */
export const storeAutoCompleteModelSchema = z.object({
	id: z.string(),
	displayString: z.string(),
	thirdPartyCustomerId: z.string(),
	customerCode: z.string(),
	countryCode: countryCodeSchema,
}) satisfies z.ZodType<StoreAutoCompleteModel>;

export const locationTypeSchema = z.enum(['Store', 'Corporate', 'Other']);

/** @see: AppleRetail.Web.Features.Stores.StoreListingController.StoreListingModel */
export const baseStoreListingModelSchema = z.object({
	id: z.string(),
	name: z.string(),
	vintage: z.string().nullable(),
	code: z.string(),
	streetAddress1: z.string(),
	streetAddress2: z.string(),
	streetAddress3: z.string(),
	city: z.string(),
	stateOrProvince: z.string(),
	postalCode: z.string(),
	countryCode: countryCodeSchema.nullable(),
	customerGroupId: z.string(),
	customerGroupName: z.string(),
	status: z.string(),
	activeDate: dateTimeSchema.nullable(),
	inactiveDate: dateTimeSchema.nullable(),
	storeNumber: z.string(),
	logoImageUrl: z.string().nullable(),
	thirdPartyId: z.string(),
	timeZone: z.string(),
	costCenter: z.string().nullable(),
	goLiveDate: dateTimeSchema.nullable(),
	expeditedShippingFee: z.number().nullable(),
	recipientName: z.string().nullable(),
	attentionTo: z.string().nullable(),
	warehouseLocationId: z.string(),
	utcOffsetMinutes: z.number(),
	locationType: locationTypeSchema,
});

export const storeListingModelSchema = baseStoreListingModelSchema as z.ZodType<StoreListingModel>;

/** Manzanita.Collections.PaginatedList<StoreListingModel> */
export const storePaginatedListSchema = paginatedListDtoSchema(storeListingModelSchema);

/** @see: AppleRetail.Web.Models.StoreBudgetModel */
const fiscalPeriodBudgetSchema = z.object({
	periodNumber: z.number(),
	allocatedAmount: z.number(),
	consumedAmount: z.number(),
}) satisfies z.ZodType<FiscalPeriodBudget>;

/** @see: AppleRetail.Web.Models.FiscalYearBudgetModel */
const fiscalYearBudgetSchema = z.object({
	fiscalYear: z.number(),
	storedBudgets: z.array(fiscalPeriodBudgetSchema),
}) satisfies z.ZodType<FiscalYearBudget>;

/** @see: AppleRetail.Web.Features.Stores.StoreModel */
const storeModel = z.object({
	id: z.string(),
	name: z.string(),
	vintageId: z.number().nullable(),
	customerCode: z.string(),
	streetAddress1: z.string(),
	streetAddress2: z.string(),
	streetAddress3: z.string(),
	city: z.string(),
	stateOrProvince: z.string(),
	postalCode: z.string(),
	countryCode: countryCodeSchema,
	activeDate: dateTimeSchema.nullable(),
	inactiveDate: dateTimeSchema.nullable(),
	logoImageUrl: z.string(),
	costCenter: z.string().nullable(),
	recipientName: z.string(),
	attentionTo: z.string().nullable(),
	goLiveDate: dateTimeSchema.nullable(),
	timeZone: z.string(),
	thirdPartyCustomerId: z.string(),
	phone: z.string().nullable(),
	countryCodes: z.array(z.string()),
	// Should rename property when old front-end retired
	customerClassification: locationTypeSchema,
	customerGroup: customerGroupSchema,
	currencyCode: z.string(),
	fiscalYearBudgets: z.array(fiscalYearBudgetSchema),
	cultureCode: z.string().nullable(),
	emailAddress: z.string().nullable(),
	shipmentDays: z.array(weekdayOptionSchema),
	weekDays: z.array(weekdayOptionSchema),
	cnpj: z.string().nullable(),
	ie: z.string().nullable(),
	isIeExempt: z.boolean(),
	orderCutoffTime: z.number(),
	cityStatePostalDisplay: z.string(),
	countryNameDisplay: z.string(),
	storeNumber: z.string(),
	isDeleted: z.boolean(),
});

/** @see: AppleRetail.Web.Features.Stores.StoreModel */
export const storeModelSchema = storeModel as z.ZodType<StoreModel>;

export const storeModelRequestSchema = storeModel.extend({
	id: z.string().nullable(),
	customerClassification: locationTypeSchema.nullable(),
	phone: z.string(),
	emailAddress: z.string(),
}) as z.ZodType<StoreModelRequest>;

// This is a schema for the form data that is coming from the validation api 'CustomerAdministration'
export const storeFormData = storeModel
	.extend({
		recipient: storeModel.shape.recipientName,
		storeId: storeModel.shape.thirdPartyCustomerId,
		storeName: storeModel.shape.name,
		addressLineOne: storeModel.shape.streetAddress1,
		addressLineTwo: storeModel.shape.streetAddress2,
		addressLineThree: storeModel.shape.streetAddress3,
		stateProvinceRegion: storeModel.shape.stateOrProvince,
		country: storeModel.shape.countryCode,
		phoneNumber: z.string(),
		imageFileName: storeModel.shape.logoImageUrl,
		locationType: storeModel.shape.customerClassification,
	})
	.omit({
		recipientName: true,
		name: true,
		streetAddress1: true,
		streetAddress2: true,
		streetAddress3: true,
		stateOrProvince: true,
		countryCode: true,
		thirdPartyCustomerId: true,
		phone: true,
		logoImageUrl: true,
		customerClassification: true,
	});

export const storeFormDataSchema = storeFormData as z.ZodType<StoreFormData>;

export const storeBulkUploadItemSchema =
	bulkUploadItemSchema satisfies z.ZodType<StoreBulkUploadItem>;

export const storeBulkUploadResponseSchema = createBulkUploadResponseSchema(
	storeBulkUploadItemSchema,
) satisfies z.ZodType<StoreBulkUploadResponse>;

/** AppleRetail.Web.Features.Stores.StoreListingController.Market */
const marketSchema = z.object({
	/** This actually returns the market (customer group) id, not the code... */
	code: z.string(),
	name: z.string(),
	countryCode: countryCodeSchema,
});

/** AppleRetail.Web.Features.Stores.StoreListingController.StoreListingResponse */
export const storeListingResponseSchema = z.object({
	storeListingModels: storePaginatedListSchema,
	availableMarkets: z.array(marketSchema),
	availableCountries: z.array(z.string()),
});

/** AppleRetail.Web.Features.Stores.StoreListingController.StoreListingRequest */
export const storeRequestSchema = z.object({
	currentPage: z.number(),
	pageSize: z.number(),
	searchText: z.string().optional(),
	selectedMarketId: z.string().optional(),
	countryCode: countryCodeSchema.optional(),
	storeCodesToExclude: z.array(z.string()).optional(),
	storeCodes: z.array(z.string()).optional(),
	includeInactiveStores: z.boolean().optional(),
	locationType: locationTypeSchema.optional(),
});
